import React, { useEffect, useMemo, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLearnedTerms } from '../contexts/LearnedTermsContext';
import { useGlobalModal } from '../contexts/ModalContext';
import useHasEverAuthenticated from '../hooks/useHasEverAuthenticated';
import { buildTrackedAuthRedirectUrl } from '../analytics';
import Button from './Button';
import FaIcon from './FaIcon';
import styles from '../styles/LearnedTermsInfoModal.module.css';
import modalStyles from '../styles/modal.module.css';
import buttonStyles from '../styles/Button.module.css';

const LearnedTermsInfoModal = () => {
  const { isSignedIn } = useUser();
  const hasEverAuthenticated = useHasEverAuthenticated();
  const navigate = useNavigate();
  const location = useLocation();
  const { closeModal } = useGlobalModal();
  const {
    learnedTermsCount,
    learnedTerms,
    isLoading: learnedTermsLoading,
  } = useLearnedTerms();
  const safeCount = isSignedIn ? Math.max(0, Number(learnedTermsCount) || 0) : 0;
  const chartData = learnedTerms;
  const isChartLoading = isSignedIn && learnedTermsLoading;

  const chartDays = useMemo(() => {
    const days = Array.isArray(chartData?.days) ? chartData.days : [];
    if (!days.length) return [];
    const maxCount = Math.max(
      1,
      ...days.map((item) => Math.max(0, Number(item?.count) || 0)),
    );
    return days.map((day) => {
      const count = Math.max(0, Number(day?.count) || 0);
      const [year, month, date] = String(day?.date || '').split('-').map(Number);
      const parsedDate = Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(date)
        ? new Date(Date.UTC(year, month - 1, date))
        : null;
      const label = parsedDate
        ? new Intl.DateTimeFormat('it-IT', { weekday: 'short', timeZone: 'UTC' })
          .format(parsedDate)
          .replace('.', '')
        : '';
      const barHeightPct = Math.max(0, Math.round((count / maxCount) * 100));

      return {
        date: day?.date,
        count,
        label,
        barHeightPct,
      };
    });
  }, [chartData]);
  const lastKnownChartDaysRef = useRef([]);

  useEffect(() => {
    if (chartDays.length > 0) {
      lastKnownChartDaysRef.current = chartDays;
    }
  }, [chartDays]);

  const effectiveChartDays = chartDays.length > 0
    ? chartDays
    : lastKnownChartDaysRef.current;
  const showLoadingSkeleton = isChartLoading && effectiveChartDays.length === 0;
  const showChart = effectiveChartDays.length > 0;
  const showUnavailable = !isChartLoading && effectiveChartDays.length === 0;
  
  const handleAuthClick = () => {
    closeModal();
    const currentPath = location.pathname + location.search;
    const message = hasEverAuthenticated
      ? 'Accedi al tuo account per salvare il Contatore di parole.'
      : 'Crea un account gratuito per salvare il Contatore di parole.';
    const authUrl = buildTrackedAuthRedirectUrl({
      cta_kind: hasEverAuthenticated ? 'sign_in' : 'sign_up',
      source_surface: 'learned_terms_info_modal',
      gate_reason: 'word_counter_requires_auth',
      next_path: currentPath,
      message,
      is_returning_device: hasEverAuthenticated,
      track_gate_viewed: true,
      trigger: 'modal_auth_cta',
    });
    navigate(authUrl);
  };

  return (
    <div className={modalStyles.modalContent}>
      <div className={styles.header}>
        <div className={styles.counterDisplay}>
          <span className={styles.counterNumber}>{safeCount.toLocaleString('it-IT')}</span>
          <span className={styles.counterIcon} aria-hidden="true">
            <FaIcon className={`fa-solid fa-learning-books ${styles.counterIconGlyph}`} />
          </span>
        </div>
      </div>

      <div className={styles.content}>
        <p className={styles.description}>
          Questo strumento conta le parole e le espressioni che impari quando completi una partita con successo.
        </p>
        {!isSignedIn && (
          <Button
            primary
            className={`${styles.joinButton} ${buttonStyles.primaryBlue} ${buttonStyles.inline}`}
            onClick={handleAuthClick}
          >
            {hasEverAuthenticated ? 'Accedi al tuo account' : 'Inizia a salvare i tuoi progressi'}
          </Button>
        )}

        <div className={styles.chartCard}>
          <p className={styles.chartTitle}>Parole imparate negli ultimi 7 giorni</p>
          {isSignedIn ? (
            <>
              {showLoadingSkeleton && (
                <div className={`${styles.chartGrid} ${styles.skeletonChartGrid}`} aria-hidden="true">
                  {[26, 44, 35, 62, 41, 54, 38].map((height, index) => (
                    <div key={index} className={styles.chartColumn}>
                      <div className={styles.chartBarArea}>
                        <div
                          className={styles.chartSkeletonBar}
                          style={{ height: `${height}%` }}
                        />
                      </div>
                      <span className={styles.chartSkeletonValue} />
                      <span className={styles.chartSkeletonLabel} />
                    </div>
                  ))}
                </div>
              )}
              {showChart && (
                <>
                  <div
                    className={styles.chartGrid}
                    role="img"
                    aria-label="Grafico parole imparate negli ultimi 7 giorni"
                  >
                    {effectiveChartDays.map((day) => (
                      <div key={day.date} className={styles.chartColumn}>
                        <div className={styles.chartBarArea}>
                          <div
                            className={styles.chartBar}
                            style={{ height: `${day.barHeightPct}%` }}
                          />
                        </div>
                        <span className={styles.chartValue}>{day.count}</span>
                        <span className={styles.chartLabel}>{day.label}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {showUnavailable && (
                <p className={styles.chartStateText}>Grafico non disponibile al momento.</p>
              )}
            </>
          ) : (
            <div className={styles.lockedChartContainer}>
              <div className={styles.lockedChartPreview} aria-hidden="true">
                {[24, 46, 32, 60, 38, 52, 44].map((height, index) => (
                  <div key={index} className={styles.lockedChartColumn}>
                    <div className={styles.lockedChartBarArea}>
                      <div className={styles.lockedChartBar} style={{ height: `${height}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className={styles.lockedOverlay}>
                <span className={styles.lockedIcon} aria-hidden="true">
                  <FaIcon className="fa-solid fa-lock" />
                </span>
                <p className={styles.lockedText}>
                  Accedi per vedere il tuo andamento settimanale.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LearnedTermsInfoModal;
